import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { authAPI, IMAGE_BASE_URL } from '../api';

// Convert image URL to base64 for offline/reliable receipt printing
const convertLogoToBase64 = async (logoPath) => {
  if (!logoPath) return null;
  try {
    const response = await fetch(`${IMAGE_BASE_URL}${logoPath}`);
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch (err) {
    console.warn('Failed to convert logo to base64:', err);
    return null;
  }
};

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const initAuth = async () => {
      // Check for impersonation token in URL
      const urlParams = new URLSearchParams(window.location.search);
      const impersonateToken = urlParams.get('impersonate');

      if (impersonateToken) {
        // Use impersonation token – sessionStorage is tab-scoped, won't affect admin tab
        try {
          sessionStorage.setItem('impersonationToken', impersonateToken);
          sessionStorage.setItem('isImpersonating', 'true');
          setToken(impersonateToken);
          setIsImpersonating(true);

          // API interceptor reads sessionStorage first, so getMe() will use the impersonation token
          const response = await authAPI.getMe();
          const impersonatedUser = { ...response.data.user, isImpersonating: true };
          setUser(impersonatedUser);

          // Store user in sessionStorage too (tab-scoped)
          sessionStorage.setItem('impersonatedUser', JSON.stringify(impersonatedUser));

          // Remove impersonate param from URL without refresh
          const url = new URL(window.location.href);
          url.searchParams.delete('impersonate');
          window.history.replaceState({}, '', url.pathname);
        } catch (err) {
          console.error('Impersonation failed:', err);
          setError('Impersonation failed. The token may be expired.');
          sessionStorage.removeItem('impersonationToken');
          sessionStorage.removeItem('isImpersonating');
        }
        setLoading(false);
        return;
      }

      // Check for existing impersonation session in this tab (e.g., page refresh)
      const existingImpersonation = sessionStorage.getItem('impersonationToken');
      if (existingImpersonation) {
        try {
          setToken(existingImpersonation);
          setIsImpersonating(true);
          const response = await authAPI.getMe();
          const impersonatedUser = { ...response.data.user, isImpersonating: true };
          setUser(impersonatedUser);
          setLoading(false);
          return;
        } catch (err) {
          // Impersonation session expired, clear and fall through to normal auth
          sessionStorage.removeItem('impersonationToken');
          sessionStorage.removeItem('isImpersonating');
          sessionStorage.removeItem('impersonatedUser');
        }
      }

      // Normal auth init
      const savedToken = localStorage.getItem('token');
      const savedUser = localStorage.getItem('user');
      const savedImpersonating = localStorage.getItem('isImpersonating') === 'true';

      if (savedToken && savedUser) {
        // Parse the saved user first
        let parsedUser;
        try {
          parsedUser = JSON.parse(savedUser);
        } catch (e) {
          console.error('Auth: Failed to parse saved user data');
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('isImpersonating');
          setLoading(false);
          return;
        }

        // If offline, use saved user data without verification
        if (!navigator.onLine) {
          console.log('Auth: Offline - using cached user session');
          setUser(parsedUser);
          setToken(savedToken);
          setIsImpersonating(savedImpersonating);
          setLoading(false);
          return;
        }

        // Online - verify token with server
        try {
          const response = await authAPI.getMe();
          setUser(response.data.user);
          setToken(savedToken);
          setIsImpersonating(savedImpersonating);
          // Update cached user data with fresh data
          localStorage.setItem('user', JSON.stringify(response.data.user));
        } catch (err) {
          console.error('Auth: Token verification failed', err.response?.status, err.message);

          // Check if it's a network error vs auth error
          if (!err.response) {
            // Network error - keep user logged in with cached data
            console.log('Auth: Network error - using cached session');
            setUser(parsedUser);
            setToken(savedToken);
            setIsImpersonating(savedImpersonating);
          } else if (err.response?.status === 401) {
            // Token is invalid or expired - clear and logout
            console.log('Auth: Token invalid (401) - clearing session');
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('isImpersonating');
          } else {
            // Other server error (500, etc) - keep user logged in with cached data
            console.log('Auth: Server error - using cached session');
            setUser(parsedUser);
            setToken(savedToken);
            setIsImpersonating(savedImpersonating);
          }
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  // Auto-refresh user data when coming back online
  useEffect(() => {
    const handleOnline = async () => {
      const savedToken = localStorage.getItem('token');
      if (savedToken && user) {
        try {
          console.log('Back online - refreshing user session');
          const response = await authAPI.getMe();
          setUser(response.data.user);
          localStorage.setItem('user', JSON.stringify(response.data.user));
        } catch (err) {
          // If auth fails, only logout on 401
          if (err.response?.status === 401) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('isImpersonating');
            setUser(null);
            setToken(null);
          }
        }
      }
    };

    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [user]);

  // Convert logo to base64 for reliable receipt printing
  useEffect(() => {
    const loadLogoBase64 = async () => {
      if (user?.tenant?.businessLogo && !user?.tenant?.businessLogoBase64) {
        const base64 = await convertLogoToBase64(user.tenant.businessLogo);
        if (base64) {
          const updatedUser = {
            ...user,
            tenant: { ...user.tenant, businessLogoBase64: base64 }
          };
          setUser(updatedUser);
          localStorage.setItem('user', JSON.stringify(updatedUser));
        }
      }
    };
    loadLogoBase64();
  }, [user?.tenant?.businessLogo]);

  // Inactivity timeout - logout after 1 hour of no activity
  useEffect(() => {
    if (!user) return; // Only track when logged in

    const INACTIVITY_TIMEOUT = 60 * 60 * 1000; // 1 hour in milliseconds
    let timeoutId = null;

    const resetTimer = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        console.log('Auth: Logging out due to inactivity');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('isImpersonating');
        setToken(null);
        setUser(null);
        setIsImpersonating(false);
      }, INACTIVITY_TIMEOUT);
    };

    // Activity events to track
    const activityEvents = ['mousedown', 'keydown', 'touchstart', 'scroll'];

    // Start the timer
    resetTimer();

    // Reset timer on any activity
    activityEvents.forEach(event => {
      window.addEventListener(event, resetTimer, { passive: true });
    });

    // Cleanup
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      activityEvents.forEach(event => {
        window.removeEventListener(event, resetTimer);
      });
    };
  }, [user]);

  const login = useCallback(async (username, password, tenantSlug = null) => {
    try {
      const response = await authAPI.login(username, password, tenantSlug);

      if (!response.data) {
        return { success: false, error: 'Invalid server response' };
      }

      const { token: newToken, user: newUser } = response.data;

      if (!newToken || !newUser) {
        return { success: false, error: 'Invalid credentials or server error' };
      }

      localStorage.setItem('token', newToken);
      localStorage.setItem('user', JSON.stringify(newUser));

      setToken(newToken);
      setUser(newUser);

      return { success: true, user: newUser };
    } catch (err) {
      // Handle multiple accounts case (HTTP 409)
      if (err.response?.status === 409 && err.response?.data?.useBusinessLogin) {
        return {
          success: false,
          useBusinessLogin: true,
          error: err.response.data.message || 'Multiple accounts found. Please use business-specific login.'
        };
      }

      // Return the specific error from the server if available
      console.error('Login error details:', {
        message: err.message,
        code: err.code,
        status: err.response?.status,
        data: err.response?.data,
        url: err.config?.baseURL + err.config?.url
      });
      const serverError = err.response?.data?.error;
      const serverMessage = err.response?.data?.message;
      const networkError = !err.response ? `Network error: Unable to reach server (${err.message})` : null;
      const errorMsg = serverError || serverMessage || networkError || 'Invalid username or password';
      setError(errorMsg);
      return {
        success: false,
        error: errorMsg
      };
    }
  }, []);

  const logout = useCallback(() => {
    // If this is an impersonation tab, only clear sessionStorage – don't touch localStorage
    if (sessionStorage.getItem('isImpersonating')) {
      sessionStorage.removeItem('impersonationToken');
      sessionStorage.removeItem('isImpersonating');
      sessionStorage.removeItem('impersonatedUser');
      setToken(null);
      setUser(null);
      setIsImpersonating(false);
      // Close the impersonation tab
      window.close();
      return;
    }

    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('isImpersonating');
    setToken(null);
    setUser(null);
    setIsImpersonating(false);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const response = await authAPI.getMe();
      const updatedUser = response.data.user;
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      return updatedUser;
    } catch (err) {
      setError('Failed to refresh user data');
      return null;
    }
  }, []);

  const clearError = useCallback(() => setError(null), []);

  // Memoized role check values (not functions)
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'OWNER' || user?.isSuperAdmin;
  const isOwner = user?.role === 'OWNER' || user?.role === 'ADMIN';
  const isManager = user?.role === 'MANAGER';
  const isCashier = user?.role === 'CASHIER';
  const isKitchen = user?.role === 'KITCHEN';
  const isSuperAdmin = user?.isSuperAdmin === true;
  const canViewAnalytics = user?.role === 'ADMIN' || user?.role === 'OWNER' || user?.role === 'MANAGER' || user?.isSuperAdmin;

  // Business type helpers for salon/services features
  const businessType = user?.businessType || null;
  const isServicesType = ['SERVICES', 'SALON'].includes(user?.businessType);
  const hasStylistFeature = isServicesType;

  const value = useMemo(() => ({
    user,
    token,
    login,
    logout,
    refreshUser,
    isAdmin,
    isOwner,
    isManager,
    isCashier,
    isKitchen,
    isSuperAdmin,
    canViewAnalytics,
    businessType,
    isServicesType,
    hasStylistFeature,
    loading,
    isImpersonating,
    error,
    clearError
  }), [user, token, login, logout, refreshUser, isAdmin, isOwner, isManager, isCashier, isKitchen, isSuperAdmin, canViewAnalytics, businessType, isServicesType, hasStylistFeature, loading, isImpersonating, error, clearError]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
